import {
  USER_LIST_URLS,
  VIEW_ENTITY_TYPE_URL,
  TRUST_DIRECTORY_ENTRIES_URL,
  get_project_id,
  get_token,
  GET_ENQUIRY_RESPONSES,
  get_headers
} from '@/utils/constants/constants';
import AICampusFooter from "@/components/probaworkflows_base_components/AICampusFooter/AiFooter.vue";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from 'axios';
import Highcharts from 'highcharts';

export default {
  name: 'Overview',
  components: {
    AICampusFooter,
  },
  mixins: [alert_mixin],
  data: () => ({
    trust_types: [],
    trust_directory_entries_count: 0,
    token: '',
    project_id: '',
    users: [],
    users_list: [],
    table_headers: [],
    errorMessage: '',
    client_name: '',
    leads_list: [],
    is_loading: false,
    status_counts: {},
  }),
  async created() {
    try {
      this.client_name = sessionStorage.getItem('company_name');
      this.token = await get_token();
      this.project_id = await get_project_id();

      await this.view_created_trust_type();
      await this.fetchUsers();
      await this.get_all_leads();
    } catch (error) {
      this.show_error('Error during initialization');
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.renderTrustChart();
    });
  },
  methods: {
    renderTrustChart() {
      Highcharts.setOptions({
        lang: {
          decimalPoint: '.',
          thousandsSep: ','
        }
      });
      Highcharts.chart(this.$refs.trustChartContainer, this.getChartOptions());
    },
    getChartOptions: () => ({
      title: { text: 'Client Growth', align: 'left' },
      subtitle: { text: 'Client statistics over time', align: 'left' },
      yAxis: { title: { text: 'Number of Clients' } },
      xAxis: {
        accessibility: { rangeDescription: 'Range: 2020 to 2024' },
        categories: ['2020', '2021', '2022', '2023', '2024']
      },
      legend: { layout: 'vertical', align: 'right', verticalAlign: 'middle' },
      plotOptions: { series: { label: { connectorAllowed: false } } },
      series: [
        { name: 'New Clients', data: [90, 60, 10, 80, 190], color: '#B7E863FF', lineWidth: 3 },
        { name: 'Active Clients', data: [10, 20, 35, 50, 75], color: '#2C3D59FF', lineWidth: 3 },
        { name: 'Inactive Clients', data: [5, 8, 80, 18, 15], color: '#B7E863FF', lineWidth: 3 }
      ],
      responsive: {
        rules: [{
          condition: { maxWidth: 500 },
          chartOptions: {
            legend: { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' }
          }
        }]
      },
      credits: { enabled: false }
    }),

    async view_created_trust_type() {
      const project_id = parseInt(this.project_id, 10);
      if (!this.validate_non_sub_zero_number(project_id)) {
        this.show_error('Project Id must not be null');
        return;
      }

      const params = { project_id };
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Token ${this.token}`,
      };

      try {
        let response = await axios.get(VIEW_ENTITY_TYPE_URL, { headers, params });
        if (response.data.status === 'success') {
          this.trust_types = response.data.data;
        } else {
          this.show_error('Error during the form submit');
        }
      } catch (error) {
        this.show_error(error.response?.data?.message || 'An unexpected error occurred');
      }
    },

    async fetchTrustDirectoryEntries() {
      const params = { project_id: this.project_id };
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Token ${this.token}`,
      };

      try {
        const response = await axios.get(TRUST_DIRECTORY_ENTRIES_URL, { headers, params });
        if (response.data.status === 'success') {
          const entries = response.data.data || [];
          this.trust_directory_entries_count = entries.length;
        } else {
          this.show_error(response.data.message || 'Failed to fetch trust directory entries');
        }
      } catch (error) {
        this.show_error('Error fetching trust directory entries: ' +
          (error.response?.data?.message || error.message));
      }
    },

    async fetchUsers() {
      this.errorMessage = '';
      try {
        const response = await axios.get(USER_LIST_URLS, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${this.token}`,
          },
          project_id: this.project_id
        });
        const res = JSON.parse(response.data);
        this.users = res.data.map(user => ({
          user_id: user.id,
          name: user.user.first_name + ' ' + user.user.last_name,
          email: user.user.email,
          last_login: user.user.last_login,
          role: user.user.role.role,
        }));
        this.users_list = this.users;
        this.table_headers = Object.keys(this.users_list[0]);
      } catch (error) {
        this.errorMessage = 'Error fetching users: ' + error.message;
      }
    },

    get_all_leads() {
      this.is_loading = true;
      const params = { project_id: this.project_id };
      axios.get(GET_ENQUIRY_RESPONSES, { params, headers: get_headers() })
        .then(response => {
          if (response.data.status === 'success') {
            this.leads_list = this.process_leads_data(response.data.data);
            this.update_status_counts();
          } else {
            this.show_error('Failed to load leads');
          }
        })
        .catch(error => {
          this.show_error(`Error fetching leads: ${error.response?.data?.message}`);
        })
        .finally(() => {
          this.is_loading = false;
        });
    },

    process_leads_data(data) {
      return data;
    },

    update_status_counts() {
      const counts = new Map();
      this.leads_list.forEach(lead => {
        const status = lead.status || 'unknown';
        counts.set(status, (counts.get(status) || 0) + 1);
      });
      this.status_counts = Object.fromEntries(counts);
    },

    async getAllLeads() {
      try {
        this.is_loading = true;
        const params = { project_id: this.project_id };
        const response = await axios.get(GET_ENQUIRY_RESPONSES, { params, headers: get_headers() });

        if (response.data.status === 'success') {
          this.leads_list = this.process_leads_data(response.data.data);
          this.update_status_counts();
        } else {
          this.show_error('Failed to load leads');
        }
      } catch (error) {
        this.show_error(`Error fetching leads: ${error.response?.data?.message}`);
      } finally {
        this.is_loading = false;
      }
    },

    validate_non_sub_zero_number(value) {
      return value !== null && value !== undefined && !isNaN(value) && value > 0;
    },
  },
  computed: {
    total_trust_count() {
      return this.trust_types.length;
    },
    total_user_count() {
      return this.users.length;
    },
    total_lead_count() {
      return this.leads_list.length;
    },
    total_trust_directory_count() {
      return this.trust_directory_entries_count;
    },
  },
};
